import React, { useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const AddCustomer = ({ isOpen, onClose, onCustomerAdded }) => {
  const [farmname, setFarmname] = useState("");
  const [emailid, setEmailid] = useState("");
  const [mobile, setMobile] = useState("");
  const [validityMonths, setValidityMonths] = useState(""); // Changed from expiresAt to validityMonths
  const [licenseKey, setLicenseKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

  // Generate month options (1-12)
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  const generateLicenseKey = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/superadmin/generate_license_key/`
      );
      if (response.data.license_key) {
        setLicenseKey(response.data.license_key);
      }
    } catch (error) {
      toast.error("Failed to generate license key");
      console.error("Error:", error);
    }
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    if (!farmname || !mobile || !emailid || !licenseKey || !validityMonths) {
      toast.error("Please fill all the fields");
      return;
    }

    if (!emailRegex.test(emailid)) {
      toast.error("Please Enter a Valid Email ID");
      return;
    }

    const userData = {
      farm_name: farmname,
      email: emailid,
      mobile: mobile,
      license_key: licenseKey,
      validity_months: parseInt(validityMonths),
    };

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/superadmin/add_customer/`,
        userData,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        setIsLoading(false);
        toast.success("Customer added successfully!");
        resetForm();
        onClose();
        onCustomerAdded();
      } else {
        setIsLoading(false);
        toast.error(response.message || "Failed to add user");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error:", error);

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong!");
      }
    }
  };

  const resetForm = () => {
    setFarmname("");
    setEmailid("");
    setMobile("");
    setValidityMonths(""); // Reset validityMonths instead of expiresAt
    setLicenseKey("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-80 z-40 transition-opacity"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-[#1f1f1f] rounded-lg shadow-2xl w-full max-w-[800px] max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h3 className="text-xl font-semibold text-white">
              Add New Customer
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6">
            <form onSubmit={handleSubmitUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Farm Name */}
                <div>
                  <label
                    htmlFor="farmname"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Farm Name *
                  </label>
                  <input
                    type="text"
                    id="farmname"
                    value={farmname}
                    onChange={(e) => setFarmname(e.target.value)}
                    className="w-full px-3 py-2 bg-[#282828] border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter farm name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="emailid"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="text"
                    id="emailid"
                    value={emailid}
                    onChange={(e) => setEmailid(e.target.value)}
                    className="w-full px-3 py-2 bg-[#282828] border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email address"
                  />
                </div>

                {/* Mobile */}
                <div>
                  <label
                    htmlFor="mobile"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Mobile Number *
                  </label>
                  <input
                    type="text"
                    id="mobile"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className="w-full px-3 py-2 bg-[#282828] border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter mobile number"
                  />
                </div>

                {/* License Key */}
                <div>
                  <label
                    htmlFor="licenseKey"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    License Key *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="licenseKey"
                      value={licenseKey}
                      readOnly
                      className="flex-1 px-3 py-2 bg-[#282828] border border-gray-600 rounded-md text-gray-300 cursor-not-allowed"
                      placeholder="Click generate to create license key"
                    />
                    <button
                      type="button"
                      onClick={generateLicenseKey}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200"
                    >
                      Generate
                    </button>
                  </div>
                </div>

                {/* Validity Period (Months) */}
                <div className="md:col-span-2">
                  <label
                    htmlFor="validityMonths"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Validity Period (Months) *
                  </label>
                  <select
                    id="validityMonths"
                    value={validityMonths}
                    onChange={(e) => setValidityMonths(e.target.value)}
                    className="w-full max-w-xs px-3 py-2 bg-[#282828] border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select months</option>
                    {monthOptions.map((month) => (
                      <option key={month} value={month}>
                        {month} {month === 1 ? 'month' : 'months'}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Select validity period in months (1-12)
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-700">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors duration-200"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Adding...
                    </span>
                  ) : (
                    "Add Customer"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCustomer;